import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismInput, PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import type { PropertyOption } from 'lumen/components/settings/users/property-select';
import PropertySelect from 'lumen/components/settings/users/property-select';
import { PRODUCTS } from 'lumen/utils/constants/users-constants';

interface FilterOption extends Option {
  id: string | null;
}

interface InlineFiltersSignature {
  Args: {
    isAllProperties: boolean;
    onProductChange: (product: string | null) => void;
    onPropertiesChange: (property: PropertyOption | null) => void;
    onRoleChange: (role: string | null) => void;
    onSearchChange: (event: Event) => void;
    product: string | null;
    properties: PropertyOption | null;
    role: string | null;
    search: string;
    userCount: number;
  };
}

export default class InlineFilters extends Component<InlineFiltersSignature> {
  @service declare intl: Services['intl'];

  @cached
  get productOptions(): FilterOption[] {
    return [
      {
        id: null,
        label: this.intl.t('settings.users.filter.all_products'),
      },
      {
        id: PRODUCTS.BI_BENCHMARKING,
        label: this.intl.t('settings.users.column.bi_benchmarking'),
      },
      {
        id: PRODUCTS.RATE_INSIGHT,
        label: this.intl.t('settings.users.column.rate_insight_market_insight'),
      },
    ];
  }

  @cached
  get roleOptions(): FilterOption[] {
    return [
      {
        id: null,
        label: this.intl.t('settings.users.filter.all_roles'),
      },
      { id: 'admin', label: this.intl.t('useraccess_rights_admin') },
      { id: 'write', label: this.intl.t('useraccess_rights_write') },
      { id: 'read', label: this.intl.t('useraccess_rights_read') },
    ];
  }

  get selectedProduct(): FilterOption {
    return (
      this.productOptions.find((option) => option.id === this.args.product) ??
      this.productOptions[0]!
    );
  }

  get selectedRole(): FilterOption {
    return (
      this.roleOptions.find((option) => option.id === this.args.role) ??
      this.roleOptions[0]!
    );
  }

  @action
  updateProduct(option: FilterOption): void {
    this.args.onProductChange(option.id);
  }

  @action
  updateRole(option: FilterOption): void {
    this.args.onRoleChange(option.id);
  }

  <template>
    {{#if @isAllProperties}}
      <span
        class="text-300--regular mr-auto"
        data-test-id="aggregated-user-count"
      >
        {{t "settings.users.count" count=@userCount}}
      </span>
    {{/if}}
    <div>
      <PrismInput
        @iconAfter={{hash name="search"}}
        @onInput={{@onSearchChange}}
        @placeholder={{t "settings.users.filter.search_placeholder"}}
        @removePadding={{true}}
        @value={{@search}}
      />
    </div>
    <PropertySelect @onChange={{@onPropertiesChange}} @value={{@properties}} />
    <PrismSelectInput
      @onChange={{this.updateRole}}
      @options={{this.roleOptions}}
      @placeholder={{t "settings.users.filter.all_roles"}}
      @value={{this.selectedRole}}
    />
    <PrismSelectInput
      @onChange={{this.updateProduct}}
      @options={{this.productOptions}}
      @placeholder={{t "settings.users.filter.all_products"}}
      @value={{this.selectedProduct}}
    />
  </template>
}
